import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { Radio, RadioGroup } from '@headlessui/react';
import { setNoiseSuppressionEngine } from 'flow/actions/headset-actions';
import BetaBadge from 'flow/components/badges/BetaBadge';
import Icon from 'flow/components/ui-components/Icon';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectLocalCallIsOngoing } from 'flow/selectors/call-selectors';
import { selectNoiseSuppressionEngine } from 'flow/selectors/headset-selectors';
import { DEFAULT_NOISE_SUPPRESSION_ENGINE, isNoiseSuppressionSupported } from 'flow/utils/noise-suppression-utils';
import SettingRow from './SettingRow';
import SettingSection from './SettingSection';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* Two models rather than one, because the open question this setting exists to
   answer is whether an open-source denoiser is good enough to replace the Krisp
   licences some customers pay for — and that is a question about the strong
   model, not the cheap one. */
var ENGINE_OPTIONS = [{
  engine: 'rnnoise',
  labelKey: 'media.settings.noisesuppression.standard',
  descriptionKey: 'media.settings.noisesuppression.standard_description'
}, {
  engine: 'gtcrn',
  labelKey: 'media.settings.noisesuppression.strong',
  descriptionKey: 'media.settings.noisesuppression.strong_description'
}];
var NoiseSuppressionEngineRow = function NoiseSuppressionEngineRow(_ref) {
  var engine = _ref.engine,
    labelKey = _ref.labelKey,
    descriptionKey = _ref.descriptionKey;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var selectedEngine = useAppSelector(selectNoiseSuppressionEngine);
  var callIsOngoing = useAppSelector(selectLocalCallIsOngoing);

  /* The radio is only the label text, but its stretched pseudo-element covers
     the row that `relative` anchors, so a click anywhere in the row, hint
     included, selects it. The radio sits inside SettingRow's Field, which is
     what makes the hint its description; the check icon in `cta` stays the
     visual state. */
  return /*#__PURE__*/_jsx(SettingRow, {
    className: "relative",
    disabled: callIsOngoing,
    information: i18next.t(descriptionKey),
    cta: engine === selectedEngine ? /*#__PURE__*/_jsx(Icon, {
      iconKey: "done"
    }) : null,
    children: /*#__PURE__*/_jsx(Radio, {
      value: engine,
      className: "stretched-control cursor-pointer data-disabled:cursor-not-allowed",
      children: i18next.t(labelKey)
    })
  });
};
var NoiseSuppressionToggle = function NoiseSuppressionToggle() {
  var dispatch = useAppDispatch();
  var _useAppContext2 = useAppContext(),
    i18next = _useAppContext2.i18next;
  var engine = useAppSelector(selectNoiseSuppressionEngine);
  var callIsOngoing = useAppSelector(selectLocalCallIsOngoing);

  /* The graph is built from the track the call already sends, at `confirmed`,
     so a change made now reaches nothing that is up. Say that in the hint
     rather than accept a switch that appears to do something. */
  var information = callIsOngoing ? 'media.settings.noisesuppression.locked_during_call' : 'media.settings.noisesuppression_description';
  return /*#__PURE__*/_jsxs(SettingRow, {
    disabled: callIsOngoing,
    information: i18next.t(information),
    cta: /*#__PURE__*/_jsx(Toggle, {
      value: engine !== 'off',
      disabled: callIsOngoing,
      text: i18next.t('media.settings.noisesuppression'),
      onChange: function onChange(enabled) {
        return dispatch(setNoiseSuppressionEngine(enabled ? DEFAULT_NOISE_SUPPRESSION_ENGINE : 'off'));
      }
    }),
    children: [i18next.t('media.settings.noisesuppression'), /*#__PURE__*/_jsx(BetaBadge, {})]
  });
};
var NoiseSuppressionEngineChoice = function NoiseSuppressionEngineChoice() {
  var dispatch = useAppDispatch();
  var _useAppContext3 = useAppContext(),
    i18next = _useAppContext3.i18next;
  var selectedEngine = useAppSelector(selectNoiseSuppressionEngine);
  var callIsOngoing = useAppSelector(selectLocalCallIsOngoing);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SettingSection, {
      children: i18next.t('media.settings.noisesuppression.quality')
    }), /*#__PURE__*/_jsx(RadioGroup, {
      value: selectedEngine,
      disabled: callIsOngoing,
      "aria-label": i18next.t('media.settings.noisesuppression.quality'),
      onChange: function onChange(engine) {
        return dispatch(setNoiseSuppressionEngine(engine));
      },
      children: ENGINE_OPTIONS.map(function (option) {
        return /*#__PURE__*/_jsx(NoiseSuppressionEngineRow, _objectSpread({}, option), option.engine);
      })
    })]
  });
};
var NoiseSuppression = function NoiseSuppression() {
  var _useAppContext4 = useAppContext(),
    i18next = _useAppContext4.i18next;
  var engine = useAppSelector(selectNoiseSuppressionEngine);

  /* Nothing to offer where the browser has no AudioWorklet to run the model
     in — the call still works, it just keeps the browser's own suppressor. */
  if (!isNoiseSuppressionSupported()) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SettingSection, {
      children: i18next.t('media.settings.noisesuppression')
    }), /*#__PURE__*/_jsx(NoiseSuppressionToggle, {}), engine !== 'off' ? /*#__PURE__*/_jsx(NoiseSuppressionEngineChoice, {}) : null]
  });
};
export default NoiseSuppression;